import {
  Component,
  ChangeDetectionStrategy,
  input,
  inject, computed,
} from '@angular/core';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { IRIS_CURRENT_USER_ID, IrisUserInfo, IrisUserInfoI } from '@iris/common/modules/user-common/models/IrisUserInfo';
import { IrisUserService } from '@iris/common/services/user.service';
import { StrakitUserComponent, StrakitUserInfoI } from '@ngx-strakit/cdk/controls';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LoadUserInfo } from '@iris/common/redux/actions/users.actions';
import { Store } from '@ngrx/store';
import * as fromGlobal from '@iris/common/redux/reducers';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';

const SYSTEM_USER_ID = -1;
const SYSTEM_USER_LABEL = 'label.irisUserName';

const CURRENT_USER_LABEL = 'label.CurrentUser';

@Component({
  selector: 'sk-iris-user',
  template: `
    <sk-user
      [userInfo]="userInfo()"

      [avatarSize]="avatarSize()"
      [avatarUrl]="avatarImgUrl()"

      [showOnlyAvatar]="showOnlyAvatar()"
      [showAvatar]="showAvatar()"
      [isCurrentUser]="isCurrentUser()"
      [showCompany]="showCompany()"
      [showEmail]="showEmail()"
      [showDepartment]="showDepartment()"

      [popoverSticky]="popoverSticky()"
      [noHint]="noHint()"

      [noUserLabel]="noUserLabel() | translate"

      [cssClass]="cssClass()"
    ></sk-user>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./sk-iris-user.component.scss'],
  imports: [
    StrakitUserComponent,
    TranslatePipe,
  ],
})
export class IrisUserComponent {

  translateService = inject(TranslateService);
  irisUserService = inject(IrisUserService);
  store = inject(Store<fromGlobal.State>);
  globalSandbox = inject(IrisGlobalSandbox);
  filesService = inject(IrisFilesService);

  /** INPUTS **/
  userId = input<number>();

  showOnlyAvatar = input<boolean>(false);
  showAvatar = input<boolean>(true);
  isCurrentUser = input<boolean>(false);
  avatarSize = input<'sm' | 'md' | 'lg' | 'xl'>();

  showCompany = input<boolean>(false);
  showDepartment = input<boolean>(false);
  showEmail = input<boolean>(false);

  noUserLabel = input<string>('label.NoUser');
  noHint = input<boolean>(false);
  popoverSticky = input<boolean>(true);
  cssClass = input<string>('');

  departments = input<IrisDepartmentI[]>([]);

  /** User Info backend retrieval **/
  userData$ = toObservable<number>(this.userId).pipe(
    // Retrieve userdata based on user id
    switchMap((userId) => {
      if (this.isCurrentUserPlaceholder(userId)) {
        return of(this.createCurrentUserPlaceholder(userId));
      }

      if (userId === SYSTEM_USER_ID) {
        return of(this.createSystemUserPlaceholder());
      }

      if (userId > 0) {
        this.store.dispatch(new LoadUserInfo(userId));
        return this.store.select(fromGlobal.getGlobalUserInfoById(userId))
          .pipe(
            filter((val) => this.isUserExists(val)),
          );
      }

      return of(null);
    }),
    // Retrieve company name based on user data and combine with user data
    switchMap((user) => {
      if (user?.companyId != null) {
        return combineLatest<[IrisUserInfoI | null, string | null]>([
          of(user),
          this.globalSandbox.getCompanyById(user.companyId).pipe(
            map(company => company?.name),
          ),
        ]);
      } else {
        return of<[IrisUserInfoI | null, string | null]>([user, null]);
      }
    }),
    catchError(() => of<[IrisUserInfoI | null, string | null]>([null, null])),
    map(([user, companyName]) => ({ user, companyName })),
  );

  /** IrisUserInfo to StrakitUserInfo Signal mapper for sk-user control input **/
  private readonly userData = toSignal(this.userData$.pipe(
    map(({ user, companyName }) => this.irisUserToSkUser( user, companyName ))),
  );

  avatarImgUrl = toSignal(this.userData$.pipe(
    switchMap((data) => {
      if (data?.user?.avatarFileId != null) {
        return of(this.filesService.getFileContentUrl(data.user.avatarFileId));
      }

      return of(null);
    }),
  ));

  // User info shown by sk-user component
  userInfo = computed<StrakitUserInfoI>(() => {
    const userData = this.userData();
    if (!userData) return null;

    const department = this.departments()
      .filter((department) => department.id === userData?.departmentId?.toString())
      .map((department) => department.name)[0];

    delete userData['departmentId'];

    return { ...userData, department };
  });

  /** Private methods **/
  private isCurrentUserPlaceholder(value: number | string): boolean {
    return value === IRIS_CURRENT_USER_ID || value === 'me';
  }

  private createCurrentUserPlaceholder(id: number): IrisUserInfoI {
    return new IrisUserInfo({
      id,
      firstname: this.irisUserService.me?.firstname || '',
      lastname: this.irisUserService.me?.lastname || '',
      fullName: this.irisUserService.me?.fullName || '',
      enabled: true,
    });
  }

  private createSystemUserPlaceholder(): IrisUserInfoI {
    const systemUser = this.translateService.instant(SYSTEM_USER_LABEL);
    return new IrisUserInfo({
      id: SYSTEM_USER_ID,
      username: systemUser,
      firstname: systemUser,
    });
  }

  private isUserExists(userInf: IrisUserInfoI): boolean {
    let userExists = false;

    if (userInf) {
      // if the user was deleted from IRIS but was not unlinked from the entity
      const keys = Object.keys(userInf);
      userExists = !!keys.filter(key => key !== 'id').find(key => userInf[key] !== undefined);
    }

    return userExists;
  }

  private irisUserToSkUser(user: IrisUserInfoI | null, company: string | null): StrakitUserInfoI & { departmentId: number }  {
    let username = user?.firstname && user?.lastname ? `${user?.firstname} ${user?.lastname}` : user?.username;

    if (!user?.enabled) {
      username = `${username} (${this.translateService.instant('label.NotActive')})`;
    }

    if (this.isCurrentUserPlaceholder(user?.id)) {
      username = this.translateService.instant(CURRENT_USER_LABEL);
    }

    return {
      company,
      department: null, // Is set to null because it depends dynamically on the departments signal
      email: user?.email,
      firstname: user?.firstname,
      id: user?.id,
      lastname: user?.lastname,
      mobileNumber: user?.mobileNumber,
      phone: user?.phone,
      username,
      departmentId: user?.departmentId,
    };
  }
}
